import { useEffect, useMemo, useState } from 'react';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	PanelContent,
	StyledContainer,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/styled';
import { DataKYCFileTctWithFile, GetKYCInvestorInstanceRes } from '@dealstack/shared/lib/generated';
import { KycStepRightPanelTab } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/types';
import { ReviewKycFiles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/ReviewKycFiles';
import { ReviewKycForm } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/ReviewKycForm';
import { ReviewDeclarations } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/ReviewDeclarations';
import { FilesTab } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Files/FilesTab';
import { mapHistoryToEvent } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/History/utils/mapHistoryToEvent';
import { selectKycStepHistory } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/History/utils/selectKycStepHistory';
import { HistoryEvents } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/History/HistoryEvents';
import { useDownloadHistoryDocument } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/History/useDownloadHistoryDocument';
import {
	Step,
	StepType,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/types';
import {
	SelectedTemplate,
	isFormSubmitted,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/FormsCategoryList/types';
import { MantineTabs } from '@dealstack/frontend/shared/components/MantineTabs/MantineTabs';
import { MantineTabProps } from '@dealstack/frontend/shared/components/MantineTabs/MantineTab.types';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';

interface Props {
	currentStep: Step;
	currentInvestor: GetKYCInvestorInstanceRes | undefined;
	selectedKycFile: DataKYCFileTctWithFile | undefined;
	filesForStep: DataKYCFileTctWithFile[];
	isReadOnly?: boolean;
	selectedTemplate?: SelectedTemplate;
}

export const RightPanel = ({
	currentInvestor,
	currentStep,
	selectedKycFile,
	filesForStep,
	isReadOnly = false,
	selectedTemplate,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const { dealId } = useRequiredParams('dealId');
	const handleDocumentClick = useDownloadHistoryDocument(dealId);
	const [activeTab, setActiveTab] = useState<KycStepRightPanelTab>(KycStepRightPanelTab.REVIEW);

	const historyEvents = useMemo(
		() =>
			mapHistoryToEvent(selectKycStepHistory(currentStep, currentInvestor, selectedTemplate)),
		[currentStep, currentInvestor, selectedTemplate]
	);

	const isDeclarationsStep = currentStep?.type === StepType.DECLARATIONS;
	const isFormStep = currentStep?.type === StepType.FORM;
	const showFilesTab = !isDeclarationsStep && !isReadOnly;
	// A selected uncategorized (OTHER) file in the form step is reviewed as a file, so it also gets a Review tab.
	const showReviewTab =
		!isFormStep ||
		!!selectedKycFile ||
		(!!selectedTemplate && isFormSubmitted(selectedTemplate));

	useEffect(() => {
		if (activeTab === KycStepRightPanelTab.REVIEW && !showReviewTab) {
			setActiveTab(showFilesTab ? KycStepRightPanelTab.FILES : KycStepRightPanelTab.HISTORY);
		}
		if (activeTab === KycStepRightPanelTab.FILES && !showFilesTab) {
			setActiveTab(
				showReviewTab ? KycStepRightPanelTab.REVIEW : KycStepRightPanelTab.HISTORY
			);
		}
	}, [showReviewTab, showFilesTab, activeTab]);

	const tabs = useMemo<MantineTabProps<KycStepRightPanelTab>[]>(() => {
		const list: MantineTabProps<KycStepRightPanelTab>[] = [];
		if (showReviewTab) {
			list.push({
				label: tMipManage('kyc.review.title'),
				value: KycStepRightPanelTab.REVIEW,
			});
		}
		if (showFilesTab) {
			list.push({
				label: tMipManage('kyc.files.title'),
				value: KycStepRightPanelTab.FILES,
			});
		}
		list.push({
			label: tMipManage('kyc.history.title'),
			value: KycStepRightPanelTab.HISTORY,
		});
		return list;
	}, [showReviewTab, showFilesTab, tMipManage]);

	const panels = useMemo(() => {
		if (!currentInvestor || !currentStep) {
			return [];
		}
		const panelList: { value: KycStepRightPanelTab; content: JSX.Element }[] = [];
		if (showReviewTab) {
			const reviewContent = () => {
				if (isDeclarationsStep) {
					return (
						<ReviewDeclarations
							currentInvestor={currentInvestor}
							currentStep={currentStep}
							selectedKycFile={selectedKycFile}
							isReadOnly={isReadOnly}
						/>
					);
				}
				if (!isFormStep) {
					return (
						<ReviewKycFiles
							currentInvestor={currentInvestor}
							currentStep={currentStep}
							selectedKycFile={selectedKycFile}
							isReadOnly={isReadOnly}
							selectedTemplate={selectedTemplate}
						/>
					);
				}
				if (selectedKycFile) {
					return (
						<ReviewKycFiles
							currentInvestor={currentInvestor}
							currentStep={currentStep}
							selectedKycFile={selectedKycFile}
							isReadOnly={isReadOnly}
						/>
					);
				}
				if (!selectedTemplate) {
					return null;
				}
				return (
					<ReviewKycForm
						form={selectedTemplate}
						currentStep={currentStep}
						currentInvestor={currentInvestor}
						isReadOnly={isReadOnly}
					/>
				);
			};

			panelList.push({
				value: KycStepRightPanelTab.REVIEW,
				content: <PanelContent>{reviewContent()}</PanelContent>,
			});
		}
		if (showFilesTab) {
			panelList.push({
				value: KycStepRightPanelTab.FILES,
				content: (
					<PanelContent>
						<FilesTab
							currentInvestor={currentInvestor}
							currentStep={currentStep}
							selectedKycFile={selectedKycFile}
							filesForStep={filesForStep}
							selectedTemplate={selectedTemplate}
							isReadOnly={isReadOnly}
						/>
					</PanelContent>
				),
			});
		}
		panelList.push({
			value: KycStepRightPanelTab.HISTORY,
			content: (
				<PanelContent>
					<HistoryEvents
						historyEvents={historyEvents}
						onDocumentClick={handleDocumentClick}
					/>
				</PanelContent>
			),
		});
		return panelList;
	}, [
		currentInvestor,
		currentStep,
		isDeclarationsStep,
		showReviewTab,
		showFilesTab,
		selectedKycFile,
		selectedTemplate,
		filesForStep,
		isReadOnly,
		historyEvents,
		handleDocumentClick,
	]);

	if (!currentInvestor || !currentStep) {
		return null;
	}

	return (
		<StyledContainer>
			<MantineTabs<KycStepRightPanelTab>
				value={activeTab}
				onTabChange={(value) => value && setActiveTab(value as KycStepRightPanelTab)}
				tabs={tabs}
				panels={panels}
				onlyDividerBorder
			/>
		</StyledContainer>
	);
};
